import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Info, X } from 'lucide-react';

/**
 * InfoButton — (i) icon that shows a tooltip panel.
 * Renders via portal to document.body so it never clips.
 */
export default function InfoButton({ title, children }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  useEffect(() => {
    if (open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const panelW = 288;
      const panelH = 240;
      let top = rect.bottom + 4;
      let left = rect.left;
      // Keep panel within viewport
      if (left + panelW > window.innerWidth - 8) left = window.innerWidth - panelW - 8;
      if (left < 8) left = 8;
      if (top + panelH > window.innerHeight - 8) top = rect.top - panelH - 4;
      setPos({ top, left });
    }
  }, [open]);

  return (
    <span className="relative inline-block">
      <button
        ref={btnRef}
        onClick={() => setOpen(!open)}
        className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-korinth-border text-korinth-muted hover:bg-korinth-accent/20 hover:text-korinth-accent transition-colors ml-1"
        title={title}
      >
        <Info className="w-2.5 h-2.5" />
      </button>

      {open && createPortal(
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[9999] w-72 max-h-60 overflow-y-auto bg-korinth-surface border border-korinth-border rounded-lg shadow-2xl p-3"
            style={{ top: pos.top, left: pos.left }}
          >
            <div className="flex items-start justify-between mb-1.5">
              <h4 className="text-xs font-mono font-bold text-korinth-accent">{title}</h4>
              <button onClick={() => setOpen(false)} className="text-korinth-muted hover:text-white flex-shrink-0 ml-2">
                <X className="w-3 h-3" />
              </button>
            </div>
            <div className="text-[11px] text-korinth-text leading-relaxed space-y-1.5">
              {children}
            </div>
          </div>
        </>,
        document.body
      )}
    </span>
  );
}
